// src/utils/tasks/optimisticTaskCompletion.js
/**
 * Local "already done" overlay for protocol tasks.
 *
 * The recording pod reports a completion once the upload session is finalised,
 * but the backend's task board is projected from the measurement record and can
 * lag that call. Refetching alone therefore races the projection: the board
 * comes back still showing the task as outstanding, and nothing retries, so the
 * list only corrects itself on the next page load.
 *
 * The finalised session is also late: the measurement is already captured when
 * the upload starts, and on a phone connection those bytes can take minutes, so
 * the board is floored from the pod's first upload report rather than its last.
 * Entries are keyed by the upload that earned them, so the repeated reports for
 * one upload count once and a failed upload gives its floor back.
 *
 * Questionnaires do not have this problem because the submission that completes
 * the task is the same write the board reads.
 *
 * This module records the count a task is *known* to have reached and floors
 * the fetched board with it. Once the backend catches up the floor is a no-op,
 * so no cleanup pass is needed — entries simply stop mattering.
 */

import { PROTOCOL_TASK_STATES } from "../protocolTasks";

/**
 * Long enough for the projection to catch up with a finished upload, short
 * enough that a stale entry cannot survive into a new protocol period, where a
 * task's completedCount legitimately resets and a floor would wrongly inflate.
 */
export const OPTIMISTIC_COMPLETION_TTL_MS = 10 * 60 * 1000;

function getCompletedCount(task) {
  const value = Number(task?.completedCount);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function getExpectedCount(task) {
  const value = Number(task?.expectedCount);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Records one more completion of `taskId` than is currently known.
 *
 * Counts up from whichever is higher — the board's count or a floor already
 * held — so completing two recordings back to back before the backend catches
 * up registers as two, not one.
 *
 * One upload is reported several times (when it starts, again whenever it
 * resumes from a pause, and once more when it finalises), so `sourceId` names
 * the upload a floor came from and repeat reports only extend its expiry. Given
 * no `sourceId`, every call counts — the caller then owns the de-duplication.
 *
 * @param {object} completions - current overlay, keyed by task id
 * @param {string} taskId - protocol task id, e.g. "spirometry"
 * @param {object} board - the last board fetched from the backend
 * @param {{ nowMs?: number, sourceId?: string|null }} [options]
 * @returns {object} the next overlay
 */
export function recordOptimisticTaskCompletion(
  completions,
  taskId,
  board,
  { nowMs = Date.now(), sourceId = null } = {},
) {
  if (!taskId) return completions || {};

  const current = completions?.[taskId];
  const countedSourceIds = current?.sourceIds ?? [];
  const alreadyCounted =
    Boolean(sourceId) && countedSourceIds.includes(sourceId);

  const task = (board?.tasks || []).find(
    (candidate) => candidate?.taskId === taskId,
  );
  const held = current?.minCompletedCount ?? 0;
  const base = Math.max(getCompletedCount(task), held);

  return {
    ...(completions || {}),
    [taskId]: {
      // Expiry is measured from the latest report of an upload, not the first:
      // a long upload would otherwise spend most of its floor's life still
      // uploading, leaving the projection no time to catch up afterwards.
      minCompletedCount: alreadyCounted ? held : base + 1,
      expiresAtMs: nowMs + OPTIMISTIC_COMPLETION_TTL_MS,
      sourceIds:
        sourceId && !alreadyCounted
          ? [...countedSourceIds, sourceId]
          : countedSourceIds,
    },
  };
}

/**
 * Gives back the floor `sourceId` earned, for an upload that turned out not to
 * have reached the backend at all. Unknown sources are ignored, so a failure
 * reported twice — or one for an upload that was never counted — cannot pull a
 * genuine completion off the board.
 *
 * @param {object} completions - current overlay, keyed by task id
 * @param {string} taskId - protocol task id, e.g. "spirometry"
 * @param {string} sourceId - the upload whose floor is being released
 * @returns {object} the next overlay
 */
export function releaseOptimisticTaskCompletion(completions, taskId, sourceId) {
  const current = taskId ? completions?.[taskId] : null;
  if (!sourceId || !current) return completions || {};

  const countedSourceIds = current.sourceIds ?? [];
  if (!countedSourceIds.includes(sourceId)) return completions;

  const next = { ...completions };
  const minCompletedCount = Math.max(0, (current.minCompletedCount ?? 0) - 1);

  if (!minCompletedCount) {
    delete next[taskId];
    return next;
  }

  next[taskId] = {
    ...current,
    minCompletedCount,
    sourceIds: countedSourceIds.filter((id) => id !== sourceId),
  };
  return next;
}

/**
 * Folds one of the pod's upload-status reports into the overlay.
 *
 * Any report other than a failure floors the board: the recording exists from
 * the moment the upload starts, and taking the first report that arrives means a
 * missed "uploading" callback still ticks the task off when the upload
 * finalises, rather than leaving the list waiting for the next fetch.
 *
 * @param {object} completions - current overlay, keyed by task id
 * @param {{ taskId: string|null, uploadId: string|null, status: string }} report
 * @param {object} board - the last board fetched from the backend
 * @param {{ nowMs?: number }} [options]
 * @returns {object} the next overlay
 */
export function applyRecordingUploadStatus(
  completions,
  { taskId, uploadId, status } = {},
  board,
  { nowMs = Date.now() } = {},
) {
  // Without an upload to attribute it to, a floor could not be de-duplicated
  // across the reports for one upload, nor released if that upload failed.
  if (!taskId || !uploadId) return completions || {};

  if (status === "error") {
    return releaseOptimisticTaskCompletion(completions, taskId, uploadId);
  }

  return recordOptimisticTaskCompletion(completions, taskId, board, {
    nowMs,
    sourceId: uploadId,
  });
}

/**
 * Floors a fetched board with the recorded completions.
 *
 * Returns the board untouched when nothing applies, so consumers keep a stable
 * reference and do not re-render for an overlay that has caught up or expired.
 *
 * @param {object} board - board as returned by useProtocolTaskBoard
 * @param {object} completions - overlay from recordOptimisticTaskCompletion
 * @param {number} [nowMs]
 * @returns {object} the board, with just-completed tasks reflected
 */
export function applyOptimisticTaskCompletions(
  board,
  completions,
  nowMs = Date.now(),
) {
  if (!board) return board;

  const liveEntries = Object.entries(completions || {}).filter(
    ([, entry]) => entry && entry.expiresAtMs > nowMs,
  );
  if (!liveEntries.length) return board;

  const minByTaskId = Object.fromEntries(
    liveEntries.map(([taskId, entry]) => [taskId, entry.minCompletedCount]),
  );
  let changed = false;

  const applyToTask = (task) => {
    const min = minByTaskId[task?.taskId];
    if (!min) return task;

    const completed = getCompletedCount(task);
    const expected = getExpectedCount(task);
    const cap = expected ?? min;
    const next = Math.min(cap, Math.max(completed, min));

    if (next === completed) return task;

    changed = true;
    return {
      ...task,
      completedCount: next,
      state: next >= cap ? PROTOCOL_TASK_STATES.COMPLETED : task.state,
    };
  };

  const tasks = (board.tasks || []).map(applyToTask);
  if (!changed) return board;

  // recordingTasks is a filtered view of the same tasks, so it only needs the
  // same pass; questionnaires are never completed through this path.
  return {
    ...board,
    tasks,
    recordingTasks: (board.recordingTasks || []).map(applyToTask),
    summary: {
      ...(board.summary || {}),
      completedTasksCount: tasks.filter(
        (task) => task.state === PROTOCOL_TASK_STATES.COMPLETED,
      ).length,
      dueTasksCount: tasks.filter(
        (task) => task.state === PROTOCOL_TASK_STATES.DUE,
      ).length,
      overdueTasksCount: tasks.filter(
        (task) => task.state === PROTOCOL_TASK_STATES.OVERDUE,
      ).length,
    },
  };
}
